
import React, { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, Lock } from "lucide-react";
import { updatePassword } from "@/lib/auth";
import { translateAuthError } from "@/lib/auth-errors";
import { validatePassword } from "@/lib/password-validation";

interface PasswordUpdateFormProps {
  onSuccess: () => void;
  onCancel: () => void;
  // Better Auth reset-password token. Set when the user arrived here from
  // the password-reset email link via /auth/confirm. Required — without it,
  // updatePassword cannot authenticate the change.
  resetToken?: string;
}

const PasswordUpdateForm = ({ onSuccess, onCancel, resetToken }: PasswordUpdateFormProps) => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const { toast } = useToast();
  
  // Validate password on change
  useEffect(() => {
    if (password) {
      const validation = validatePassword(password);
      setPasswordErrors(validation.errors);
    } else {
      setPasswordErrors([]);
    }
  }, [password]);
  
  // Check password confirmation match
  useEffect(() => {
    if (confirmPassword && password !== confirmPassword) {
      setConfirmError("Paroolid ei ühti");
    } else {
      setConfirmError(null);
    }
  }, [password, confirmPassword]);
  
  const handleConfirmReset = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // First check for validation errors
    if (passwordErrors.length > 0) {
      toast({
        title: "Ebaturvaline parool",
        description: "Palun paranda parooliga seotud vead",
        variant: "destructive",
      });
      return;
    }
    
    // Then check if passwords match
    if (password !== confirmPassword) {
      toast({
        title: "Paroolid ei ühti",
        description: "Palun veendu, et paroolid oleksid samad",
        variant: "destructive",
      });
      return;
    }
    
    setLoading(true);

    try {
      const { error } = await updatePassword(password, resetToken);
      if (error) throw error;
      
      onSuccess();
      toast({
        title: "Parool edukalt muudetud",
        description: "Nüüd saad sisse logida oma uue parooliga",
      });
    } catch (error: any) {
      console.error("Password update error:", error);
      toast({
        title: "Parooli uuendamine ebaõnnestus",
        description: translateAuthError(error.message),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-center mb-6">Uue parooli määramine</h2>
      <form onSubmit={handleConfirmReset} className="space-y-4">
        {/* New password field */}
        <div className="space-y-2">
          <Label htmlFor="new-password">Uus parool</Label>
          <div className="relative">
            <Input
              id="new-password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="pl-10 pr-10"
              required
              minLength={8}
            />
            <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-3 text-muted-foreground"
              tabIndex={-1}
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </button>
          </div>
          
          {/* Password validation errors */}
          {passwordErrors.length > 0 && (
            <div className="text-sm text-red-500 space-y-1 mt-2">
              {passwordErrors.map((error, index) => (
                <p key={index}>{error}</p>
              ))}
            </div>
          )}
        </div>
        
        {/* Confirm password field */}
        <div className="space-y-2">
          <Label htmlFor="confirm-password">Kinnita uus parool</Label>
          <div className="relative">
            <Input
              id="confirm-password"
              type={showConfirmPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="••••••••"
              className="pl-10 pr-10"
              required
              minLength={8}
            />
            <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-3 text-muted-foreground"
              tabIndex={-1}
            >
              {showConfirmPassword ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </button>
          </div>
          
          {/* Confirm password error */}
          {confirmError && (
            <p className="text-sm text-red-500 mt-2">{confirmError}</p>
          )}
        </div>
        
        <Button 
          type="submit" 
          className="w-full mt-6"
          disabled={loading || passwordErrors.length > 0 || !!confirmError || !password || !confirmPassword}
        >
          {loading ? "Laadin..." : "Salvesta uus parool"}
        </Button>
        
        <Button 
          type="button" 
          variant="outline" 
          className="w-full"
          onClick={onCancel}
        >
          Tühista
        </Button>
      </form>
    </div>
  );
};

export default PasswordUpdateForm;
